// The dashboard's modal: a rounded panel over a dimmed page, dark in either
// theme and in the sidebar's vocabulary (its palette via data-surface="dark",
// its menus' radius, sentence-case labels, an icon to close).
// components/OverlayDialog.jsx re-exports it under its old name.
//
// Behavior:
//   • Backdrop click closes (unless `dismissible={false}`).
//   • Esc closes.
//   • Inner dialog stops propagation so clicks inside it don't dismiss.
//   • Focus moves into the dialog when it opens (a field that autofocuses
//     keeps it), Tab cycles within it, and closing returns focus to where
//     it was.
//   • Body scrolls internally when content exceeds `maxHeight`; `flush`
//     drops its padding for content that draws its own edges (a tab bar).
//   • `analyticsId` names the dialog for product analytics: it becomes the
//     autocapture attribute on every click inside and fires one
//     `$element_viewed` per open. A static id from lib/analytics/
//     surfaceVocabulary.js, never the title, which can carry tenant content.
import { useEffect, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ScrollRoot } from "./scrollRoot.js";
import { phAttr } from "../../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../../lib/analytics/surfaceVocabulary.js";
import { CaptureOnViewed } from "../../lib/analytics/react.jsx";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root) {
    return [...root.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest("[inert]") && el.getClientRects().length);
}

// Tab and Shift+Tab wrap at the dialog's ends instead of leaving it.
function trapTab(event) {
    if (event.key !== "Tab") return;
    const items = focusables(event.currentTarget);
    if (!items.length) { event.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !event.currentTarget.contains(active))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
}

export default function Dialog({
    open,
    onClose,
    eyebrow,
    title,
    width = "640px",
    maxWidth = "92vw",
    maxHeight = "82vh",
    dismissible = true,
    flush = false,
    className = "",
    analyticsId = null,
    children,
}) {
    const panelRef = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const before = document.activeElement;
        const panel = panelRef.current;
        if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
        return () => { if (before?.isConnected) before.focus?.({ preventScroll: true }); };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape" && dismissible) {
                e.stopPropagation();
                onClose?.();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, dismissible, onClose]);

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === "string" ? title : undefined}
            data-ph-capture-attribute-dialog={phAttr(ATTRIBUTE.DIALOG, analyticsId)}
            data-surface="dark"
            onClick={dismissible ? onClose : undefined}
            className="fixed inset-0 z-[10000] grid cursor-pointer place-items-center bg-black/55 p-[16px] backdrop-brightness-[0.8] backdrop-saturate-[0.6]"
        >
            <div
                ref={panelRef}
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={trapTab}
                style={{ width, maxWidth, maxHeight }}
                className={`relative grid cursor-default grid-rows-[auto_minmax(0,1fr)] overflow-hidden outline-none rounded-[12px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-[0_24px_64px_rgba(0,0,0,0.38)] ${className}`}
            >
                <header className="flex items-start justify-between gap-[12px] px-[18px] pb-[12px] pt-[15px]">
                    <div className="grid min-w-0 gap-[2px]">
                        {eyebrow ? <span className="text-[12px] leading-[1.3] text-[color:var(--muted)]">{eyebrow}</span> : null}
                        {title ? (
                            <h2 className="m-0 truncate text-[15px] font-semibold leading-[1.25] tracking-[-0.01em] text-[color:var(--ink)]">{title}</h2>
                        ) : null}
                    </div>
                    {dismissible ? (
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            title="Close"
                            data-overlay-dialog-close
                            className="-mr-[6px] -mt-[3px] grid h-[28px] w-[28px] shrink-0 cursor-pointer place-items-center rounded-[7px] border-0 bg-transparent text-[color:var(--muted)] transition-colors hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] hover:text-[color:var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)]"
                        >
                            <XMarkIcon aria-hidden="true" className="h-[16px] w-[16px]" />
                        </button>
                    ) : null}
                </header>
                <div className={`min-h-0 overflow-y-auto ${flush ? "" : "px-[18px] pb-[18px] pt-[2px]"}`}>
                    {/* The dialog's body scrolls by itself, not with the page under it. */}
                    <ScrollRoot.Provider value={null}>
                        <CaptureOnViewed name={analyticsId}>{children}</CaptureOnViewed>
                    </ScrollRoot.Provider>
                </div>
            </div>
        </div>
    );
}
